import React, { useState } from "react";
import { Progress } from "reactstrap";

import {
  ProgressBarTitleSm,
  ProgressBarTitleMd,
  ProgressBarTitleLg,
  ProgressLabel,
  ProgressStat,
} from "../styled/ProgressBar.styled";
import { useVsiAppContext } from "../stores/VsiApp";
import SummaryTooltip from "./SummaryTooltip";
import themeStyles from "../utils/themeStyles";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

const defaultThresholds = [
  [50, "#008080"],
  [70, "#0c8cff"],
  [80, "#E1D400"],
  [100, "#E50000"],
];

const handleColors = (thresholds, value) => {
  let colorChosen = thresholds[0][0]; // always using the first threshold value as default
  let thresholdToCompare; // keeping track of threshold numbers to compare later

  thresholds.forEach((thresh, index) => {
    // destructuring threshold: number comes first then the color associated
    const [num, color] = thresh;
    if (!thresholdToCompare) thresholdToCompare = num;
    // first one is always seeing if it's less than the threshold
    if (index === 0) {
      if (value < num) colorChosen = color;
    }
    // last one is always seeing if it's greater than the last threshold
    else if (index === thresholds.length - 1) {
      if (value > thresholdToCompare) colorChosen = color;
    }
    // anything else is between the bookends and we need to check if it's:
    // A. greater than or equal to the last threshold and
    // B. less than the current threshold
    else {
      if (value >= thresholdToCompare && value < num) colorChosen = color;
    }
    // assigning the threshold tracker to the current threshold value
    thresholdToCompare = num;
  });
  return colorChosen;
};

const processMultiData = data => {
  let total = 0;

  data.forEach((datum) => (total += datum));
  return data.map((datum) => ((datum / total) * 100).toFixed(3));
};

const TitleWithSize = ({size, children}) => {
  switch(size.toLowerCase()) {
    case "lg":
      return <ProgressBarTitleLg>{children}</ProgressBarTitleLg>
    case "md":
      return <ProgressBarTitleMd>{children}</ProgressBarTitleMd>
    case "sm":
    default:
      return <ProgressBarTitleSm>{children}</ProgressBarTitleSm>
  }
};

function ProgressBar({
  title,
  thresholds,
  denominator,
  isMulti,
  data,
  className,
  colors,
  multiply,
  titleFootnote,
  disableStat,
  size = "sm"
}) {
  const value = !isMulti ? (
  (multiply ? data?.[0] * 100 : data?.[0]) / 100
  ) * 100 : 0;
  const { theme } = useVsiAppContext();
  const [tooltipOpen, setTooltipOpen] = useState(false)
  
  const wholeBarStyle = size => {
    return {
      backgroundColor: themeStyles[theme].cardBgColor,
      border: "1px solid #707070",
      height: size === "sm" ? null : size === "md" ? 20 : 25,
    }
  };

  return (
    <div className={"w-100"}>
      <div className={"d-flex flex-row justify-content-between pe-4"}>
        <ProgressLabel
          // style={{ width: '25%' }}
          // className={"justify-content-start align-items-center d-flex"}
        >
          <TitleWithSize size={size}>{title || ''}</TitleWithSize>
        </ProgressLabel>
        {titleFootnote && <ProgressLabel
          // style={{ width: '25%' }}
          // className={"justify-content-start align-items-center d-flex"}
        >
          <TitleWithSize size={size}>{titleFootnote || ''}</TitleWithSize>
        </ProgressLabel>}
      </div>
      <div
      style={{ width: "100%"}}
      className={`${
        className ? className : ""
      } align-items-center d-flex`}
    >
      <div style={{ width: "100%"}}>
        {isMulti ? (
          <div>
            <Progress multi style={wholeBarStyle(size)}>
              {processMultiData(data).map((datum, index) => (
                <Progress
                  bar
                  key={`progress-bar-multi-${title
                    .toLowerCase()
                    .split(" ")
                    .join("-")}-${index}`}
                  value={datum}
                  barStyle={{
                    backgroundColor: colors[index],
                  }}
                  id={`multiBar${index}`}
                />
              ))}
            </Progress>
            {processMultiData(data).map((datum, index) => (
              <SummaryTooltip values={data} index={index} key={`progress-bar-tooltip-${title
                .toLowerCase()
                .split(" ")
                .join("-")}-${index}`}/>
            ))}
          </div>
        ) : (
          <Progress
            value={denominator === 200 ? value / 2 : value}
            style={wholeBarStyle(size)}
            barStyle={{
              backgroundColor: handleColors(
                thresholds || defaultThresholds,
                value
              ),
            }}
          />
        )}
      </div>
        {!disableStat ? (
          <ProgressStat
        className={"justify-content-center align-items-center d-flex"}
      >
        <TitleWithSize size={size}>
          <span style={{fontWeight: 'bold'}}>{(!disableStat && !isMulti && value && isFinite(value)) ? `${value.toFixed(0)}%` : null}</span>
        </TitleWithSize>
      </ProgressStat>
        ): null}
    </div>
    </div>

  );
}

ProgressBar.displayName = "ProgressBar"

export default withErrorBoundary(ProgressBar, GeneralErrorBoundary);
